import { warn } from 'cc';
import SupportLanguage from './I18n/SupportLanguage';
import I18nAssetProvider from './I18n/I18nAssetProvider';
import AssetLoadingConfigType from './Config/AssetLoadingConfigType';
import { joinURL } from './Toolkit/Utils/Utility';
import RemoteAssetProvider from './RemoteAssetProvider';
import RemoteAssetLoadingConfigType, { AssetName, assetNameToAssetTypeMap, ImageFormat } from './Config/RemoteAssetLoadingConfigType';
import LoadingTask from './Toolkit/Helper/Loading/LoadingTask/LoadingTask';
import CompositeLoadingTask from './Toolkit/Helper/Loading/LoadingTask/CompositeLoadingTask';

export default class I18nRemoteAssetProvider extends I18nAssetProvider {
    private _isRemoteAssetLoading: boolean = false;
    private _remoteAssetProviders: { [language in string]?: RemoteAssetProvider } = {};
    private _remoteAssetLoadingConfig: RemoteAssetLoadingConfigType[] = [];
    private _remoteLoadingPromises: { [language in string]?: Promise<void> } = {};
    private _loadingLanguage: SupportLanguage = null;

    constructor(
        assetLoadingConfig: AssetLoadingConfigType | AssetLoadingConfigType[],
        remoteAssetLoadingConfig: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[],
        private readonly remoteImagesFormats: ImageFormat[],
    ) {
        super(assetLoadingConfig);
        this._remoteAssetLoadingConfig = Array.isArray(remoteAssetLoadingConfig) ? remoteAssetLoadingConfig : [remoteAssetLoadingConfig];
    }

    public get isLoadingLanguage() {
        return super.isLoadingLanguage || this._isRemoteAssetLoading;
    }

    public addRemoteAssetLoadingConfig(config: RemoteAssetLoadingConfigType): this {
        this._remoteAssetLoadingConfig.push(config);
        return this;
    }

    public deleteRemoteAssetLoadingConfig(config: RemoteAssetLoadingConfigType): boolean {
        const index = this._remoteAssetLoadingConfig.indexOf(config);
        if (index === -1) {
            warn('Config not found in remote asset loading configs');
            return false;
        }
        this._remoteAssetLoadingConfig.splice(index, 1);
        return true;
    }

    public preload(): LoadingTask {
        const bundleTask = super.preload();
        const remotePromise = this._remoteLoadingPromises[this._loadingLanguage];
        if (!remotePromise) {
            throw Error(`No remote loading promise found for language: ${this._loadingLanguage}`);
        }
        const preloadTask = new CompositeLoadingTask('I18nRemoteAssetProvider_preload');
        preloadTask.addTask(bundleTask);
        preloadTask.addSimpleTask('I18nRemoteAssetProvider_remote', remotePromise);
        return preloadTask;
    }

    public async loadLanguage(language: SupportLanguage) {
        this._isRemoteAssetLoading = true;
        this._loadingLanguage = language;
        const remotePromise = super.loadLanguage(language).then(() => this._loadRemoteAssets(language));
        this._remoteLoadingPromises[language] = remotePromise;
        await remotePromise;
        this._isRemoteAssetLoading = false;
    }

    private async _loadRemoteAssets(language: SupportLanguage) {
        if (!this._remoteAssetProviders[language]) {
            this._remoteAssetProviders[language] = new RemoteAssetProvider(`I18nRemoteAssetProvider_${language}`, this.remoteImagesFormats);
        }

        const languageSpecificConfigs = this._remoteAssetLoadingConfig.map((config) => {
            const specificConfig = { ...config };
            specificConfig.prefix = joinURL(specificConfig.prefix, language);
            return specificConfig;
        });

        const configs = this._remoteAssetProviders[language].utility.getUnloadedAssetConfigs(languageSpecificConfigs);
        if (configs.length > 0) {
            const task = this._remoteAssetProviders[language].load(configs);
            await task.waitForFinish();

            if (!task.isSuccess) {
                throw new Error(`Failed to load remote assets for language: ${language}`);
            }

            // set loaded remote assets to current bundle asset provider for retrieval
            configs.forEach((config) => {
                Object.entries(config.path).forEach(([assetName, assetPathMap]) => {
                    const assetType = assetNameToAssetTypeMap[assetName as AssetName];
                    Object.keys(assetPathMap).forEach((key) => {
                        const asset = this._remoteAssetProviders[language].get(key, assetType);
                        if (!asset) {
                            throw new Error(`Failed to get loaded remote asset: ${key} of type: ${assetType} for language: ${language}`);
                        }
                        this.getProvider(language).set(key, assetType, asset);
                    });
                });
            });
        }
    }
}
